import React, { useCallback, useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import * as opexService from "../../../services/opexService";
import { getVendorCurrency } from "../../../utils/currency";
import InventoryTable from "../../Inventory/InventoryTable";
import ItemFormPanel from "./ItemFormPanel";
import CategoryManagerPanel from "./CategoryManagerPanel";
import RecordPaymentPanel from "./RecordPaymentPanel";

const TABS = ["Due & Upcoming", "All Items", "History"];

const TYPE_LABEL = { ONE_TIME: "One-time", RECURRING: "Recurring" };
const STATUS_BADGE = {
  ACTIVE: { bg: "#dcfce7", text: "#16a34a" },
  INACTIVE: { bg: "#f3f4f6", text: "#6b7280" },
  CANCELLED: { bg: "#fee2e2", text: "#dc2626" },
};

function Badge({ label, bg, text }) {
  return (
    <span style={{ padding: "3px 10px", borderRadius: "999px", fontSize: "12px", fontWeight: 600, background: bg, color: text }}>
      {label}
    </span>
  );
}

function OperatingExpenses() {
  const currency = getVendorCurrency();
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [categories, setCategories] = useState([]);

  const [reminders, setReminders] = useState([]);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const [historyFilters, setHistoryFilters] = useState({ start_date: "", end_date: "", category_id: "" });

  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [payingItem, setPayingItem] = useState(null);

  const loadCategories = useCallback(() => {
    opexService.getCategories().then((res) => setCategories(res?.data || []));
  }, []);

  const loadReminders = useCallback(() => {
    setLoading(true);
    opexService
      .getReminders()
      .then((res) => setReminders(res?.data || []))
      .finally(() => setLoading(false));
  }, []);

  const loadItems = useCallback(() => {
    setLoading(true);
    opexService
      .getItems()
      .then((res) => setItems(res?.data || []))
      .finally(() => setLoading(false));
  }, []);

  const loadPayments = useCallback(() => {
    setLoading(true);
    opexService
      .getPayments(historyFilters)
      .then((res) => setPayments(res?.data || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyFilters]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    if (activeTab === "Due & Upcoming") loadReminders();
    else if (activeTab === "All Items") loadItems();
    else if (activeTab === "History") loadPayments();
  }, [activeTab, loadReminders, loadItems, loadPayments]);

  const refreshCurrentTab = () => {
    setShowItemForm(false);
    setEditingItem(null);
    setPayingItem(null);
    if (activeTab === "Due & Upcoming") loadReminders();
    else if (activeTab === "All Items") loadItems();
    else if (activeTab === "History") loadPayments();
  };

  /* ---------------- Due & Upcoming ---------------- */
  const dueColumns = [
    { key: "title", label: "Expense" },
    { key: "category_name", label: "Category" },
    { key: "amount", label: "Amount", render: (r) => `${currency} ${Number(r.amount).toFixed(2)}` },
    { key: "type", label: "Type", render: (r) => TYPE_LABEL[r.type] },
    {
      key: "next_due_date",
      label: "Due",
      render: (r) => {
        const overdue = r.is_overdue;
        return (
          <Badge
            label={overdue ? `Overdue by ${Math.abs(r.days_until_due)}d` : r.days_until_due === 0 ? "Due today" : `Due in ${r.days_until_due}d`}
            bg={overdue || r.days_until_due === 0 ? "#fee2e2" : "#fef3c7"}
            text={overdue || r.days_until_due === 0 ? "#dc2626" : "#d97706"}
          />
        );
      },
    },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <Button size="sm" style={{ background: "var(--button-color)", border: "none", color: "#fff" }} onClick={() => setPayingItem(r)}>
          Record Payment
        </Button>
      ),
    },
  ];

  /* ---------------- All Items ---------------- */
  const itemColumns = [
    { key: "title", label: "Expense" },
    { key: "category_name", label: "Category" },
    { key: "amount", label: "Amount", render: (r) => `${currency} ${Number(r.amount).toFixed(2)}` },
    { key: "type", label: "Type", render: (r) => (r.type === "RECURRING" ? `Recurring (${r.frequency?.toLowerCase()})` : "One-time") },
    { key: "next_due_date", label: "Next Due", render: (r) => new Date(r.next_due_date).toLocaleDateString() },
    {
      key: "status",
      label: "Status",
      render: (r) => <Badge label={r.status} {...(STATUS_BADGE[r.status] || STATUS_BADGE.ACTIVE)} />,
    },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => {
              setEditingItem(r);
              setShowItemForm(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="sm"
            variant={r.status === "ACTIVE" ? "outline-danger" : "outline-success"}
            onClick={async () => {
              await opexService.setItemStatus(r.id, r.status === "ACTIVE" ? "INACTIVE" : "ACTIVE");
              loadItems();
            }}
          >
            {r.status === "ACTIVE" ? "Deactivate" : "Activate"}
          </Button>
        </div>
      ),
    },
  ];

  /* ---------------- History ---------------- */
  const historyColumns = [
    { key: "payment_date", label: "Date paid", render: (r) => new Date(r.payment_date).toLocaleDateString() },
    { key: "item_title", label: "Expense" },
    { key: "category_name", label: "Category" },
    { key: "amount_paid", label: "Amount", render: (r) => `${currency} ${Number(r.amount_paid).toFixed(2)}` },
    { key: "payment_method", label: "Method", render: (r) => r.payment_method.replace("_", " ") },
    { key: "reference_number", label: "Reference", render: (r) => r.reference_number || "—" },
    {
      key: "receipt_url",
      label: "Receipt",
      render: (r) =>
        r.receipt_url ? (
          <a href={r.receipt_url} target="_blank" rel="noopener noreferrer">
            View
          </a>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Operating Expenses</h4>
          <div style={{ fontSize: 13, color: "#6B7280" }}>Track rent, utilities and other recurring or one-time company costs.</div>
        </div>
        <Button
          style={{ background: "var(--button-color)", border: "none", color: "#fff" }}
          onClick={() => {
            setEditingItem(null);
            setShowItemForm(true);
          }}
        >
          + New Expense
        </Button>
      </div>

      <div className="d-flex gap-2 mb-3">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              border: "none",
              background: activeTab === tab ? "var(--button-color)" : "#F3F4F6",
              color: activeTab === tab ? "#fff" : "#374151",
              borderRadius: "999px",
              padding: "6px 16px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === "Due & Upcoming" && (
        <InventoryTable
          columns={dueColumns}
          data={reminders}
          loading={loading}
          showSearch={false}
          emptyMessage="Nothing due in the next 7 days."
        />
      )}

      {activeTab === "All Items" && (
        <InventoryTable columns={itemColumns} data={items} loading={loading} showSearch={false} emptyMessage="No operating expenses set up yet." />
      )}

      {activeTab === "History" && (
        <>
          <div className="d-flex gap-2 mb-3 flex-wrap">
            <Form.Control
              type="date"
              style={{ maxWidth: 180 }}
              value={historyFilters.start_date}
              onChange={(e) => setHistoryFilters((f) => ({ ...f, start_date: e.target.value }))}
              placeholder="From"
            />
            <Form.Control
              type="date"
              style={{ maxWidth: 180 }}
              value={historyFilters.end_date}
              onChange={(e) => setHistoryFilters((f) => ({ ...f, end_date: e.target.value }))}
              placeholder="To"
            />
            <Form.Select
              style={{ maxWidth: 200 }}
              value={historyFilters.category_id}
              onChange={(e) => setHistoryFilters((f) => ({ ...f, category_id: e.target.value }))}
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <InventoryTable columns={historyColumns} data={payments} loading={loading} showSearch={false} emptyMessage="No payments recorded yet." />
        </>
      )}

      <ItemFormPanel
        show={showItemForm}
        onHide={() => {
          setShowItemForm(false);
          setEditingItem(null);
        }}
        item={editingItem}
        categories={categories}
        onManageCategories={() => setShowCategoryManager(true)}
        onSaved={refreshCurrentTab}
      />

      <CategoryManagerPanel
        show={showCategoryManager}
        onHide={() => setShowCategoryManager(false)}
        categories={categories}
        onChanged={loadCategories}
      />

      <RecordPaymentPanel show={!!payingItem} onHide={() => setPayingItem(null)} item={payingItem} onSaved={refreshCurrentTab} />
    </div>
  );
}

export default OperatingExpenses;
